import React, { useEffect, useState } from 'react'
import VideoNavbar from './VideoNavbar'
import Loader from '../../components/Loader'
import Calling from '../../api/Calling'
import { toast } from 'react-toastify'

const VideoAdd = () => {
    const [loader, setLoader] = useState(false);
    const [categories, setCategories] = useState([]);
    const [videoCategory, setVideoCategory] = useState('');
    const [videos, setVideos] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);

    const fetchAllData = async () => {
        setLoader(true);
        try {
            const catRes = await Calling.getAllCategories();
            if (catRes.status) setCategories(catRes.data);
        } catch (error) {
            toast.error('Something went wrong!');
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        fetchAllData();
    }, []);

    const handleInputChange = (e) => {
        setVideoCategory(e.target.value);
    };

    const handleFileChange = (e) => {
        const selectedFiles = Array.from(e.target.files);

        // Check file count limit
        if (selectedFiles.length > 50) {
            toast.error('You can upload a maximum of 50 videos at once.');
            return;
        }

        // Filter only MP4 files and check size limit (200MB = 200 * 1024 * 1024 bytes)
        const validFiles = selectedFiles.filter(file => {
            if (file.type !== 'video/mp4') {
                toast.error(`${file.name} is not an MP4 file.`);
                return false;
            }

            if (file.size > 200 * 1024 * 1024) {
                toast.error(`${file.name} exceeds the 200MB size limit.`);
                return false;
            }

            return true;
        });

        setVideos(validFiles);
    };

    const handleUpload = async (e) => {
        e.preventDefault();

        if (!videoCategory) {
            toast.error('Please select a category.');
            return;
        }

        if (videos.length === 0) {
            toast.error('Please select at least one video to upload.');
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);

        try {
            // Create a FormData object to send files
            const formData = new FormData();
            formData.append('category', videoCategory);

            // Append all videos to FormData
            videos.forEach((video, index) => {
                formData.append('video', video);
            });

            // Define progress tracking callback
            const onProgressUpdate = (progressEvent) => {
                const percentComplete = progressEvent.percent;
                setUploadProgress(percentComplete);
            };

            // Use the addVideo function with progress tracking
            const response = await Calling.addVideo(formData, onProgressUpdate);

            // Handle the decrypted response (decryption is handled within addVideo)
            if (response && response.status) {
                toast.success('Videos uploaded successfully!');
                // Reset form
                setVideoCategory('');
                setVideos([]);
                setUploadProgress(0);
            } else {
                // Handle error
                toast.error(response?.message || 'Failed to upload videos.');
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Something went wrong during upload!');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <>
            {
                loader ? (
                    <Loader loader={loader} />
                ) : (
                    <div className='w-full text-main-font mt-20 px-2 md:px-3 md:ml-[4.4rem]'>
                        <div className="flex items-center justify-between">
                            <h1 className="text-xl md:text-2xl font-bold text-main-font mt-4 w-auto">
                                Video Management
                            </h1>
                        </div>

                        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                            <VideoNavbar selectState={'addvideo'} />
                            <hr className="border-gray-200" />

                            <form onSubmit={handleUpload}>
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h2 className="text-lg font-semibold text-gray-800 mb-4">Category Information</h2>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Category *
                                            </label>
                                            <select
                                                name="videoCategory"
                                                value={videoCategory}
                                                onChange={handleInputChange}
                                                className="w-full border border-gray-500 p-2 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                                                required
                                            >
                                                <option value="">Select Category</option>
                                                {categories.map((cat) => (
                                                    <option key={cat._id} value={cat._id}>
                                                        {cat.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg mt-4">
                                    <h2 className="text-lg font-semibold text-gray-800 mb-4">Video Upload</h2>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Upload Videos (MP4 only, max 200MB each) *
                                            </label>
                                            <div className="flex items-center justify-center w-full">
                                                <label className="flex flex-col w-full h-32 border-2 border-dashed border-gray-500 rounded-lg cursor-pointer hover:bg-gray-100 hover:border-blue-500 transition-all">
                                                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                                        <svg className="w-8 h-8 text-gray-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                                                        </svg>
                                                        <p className="text-sm text-gray-600">
                                                            <span className="font-semibold">Click to upload</span> or drag and drop
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            MP4 only (MAX. 50 files, 200MB each)
                                                        </p>
                                                    </div>
                                                    <input
                                                        type="file"
                                                        className="hidden"
                                                        accept="video/mp4"
                                                        multiple
                                                        onChange={handleFileChange}
                                                        disabled={isUploading}
                                                    />
                                                </label>
                                            </div>
                                        </div>

                                        {videos.length > 0 && (
                                            <div className="mt-4">
                                                <p className="text-sm font-medium text-gray-700 mb-2">
                                                    {videos.length} video(s) selected
                                                </p>
                                                <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2">
                                                    {videos.map((file, index) => (
                                                        <div key={index} className="text-xs text-gray-600 mb-1">
                                                            {file.name} ({(file.size / (1024 * 1024)).toFixed(2)} MB)
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Enhanced Upload Progress Bar */}
                                        {isUploading && (
                                            <div className="mt-4 space-y-2">
                                                <div>
                                                    <div className="flex justify-between items-center mb-1">
                                                        <p className="text-sm font-medium text-gray-700">Upload Progress</p>
                                                        <span className="text-xs font-medium text-blue-600">{uploadProgress}%</span>
                                                    </div>
                                                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                                                        <div
                                                            className="bg-blue-600 h-2.5 rounded-full transition-all duration-300 ease-in-out"
                                                            style={{ width: `${uploadProgress}%` }}
                                                        ></div>
                                                    </div>
                                                </div>

                                                {/* Status message */}
                                                <p className="text-xs text-gray-600">
                                                    {uploadProgress < 200
                                                        ? `Uploading ${videos.length} video(s)...`
                                                        : 'Processing uploads...'}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <button
                                        type="submit"
                                        disabled={isUploading}
                                        className={`px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    >
                                        {isUploading ? 'Uploading...' : 'Upload Videos'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }
        </>
    )
}

export default VideoAdd